import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaUserDoctor } from "react-icons/fa6";
import { TbPlayerPlayFilled } from "react-icons/tb";
import { ImStop2 } from "react-icons/im";
import { dqRulesWebSocketService, generateClientId, mapWebSocketResultToRule, type WebSocketTextToSQLRequest, type WebSocketProgressUpdate, type WebSocketCompletionResult, type WebSocketErrorResult } from '../../services/dqRulesWebSocketService';
import type { SuggestedRule } from '../../types/dqRulesLlm';
import { authManager } from '../../services/authManager';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslate } from '../../hooks/useTranslate';

// Global gate: allow only one AI modal instance open at a time across the app
let ACTIVE_MODAL_KEY: string | null = null;

/** Render basic markdown-like text (bold, bullets, newlines) as React elements */
function renderFormattedContent(text: string): React.ReactNode {
  // Normalize escaped newlines from backend JSON
  const normalized = text.replace(/\\n/g, '\n');
  const lines = normalized.split('\n');

  return lines.map((line, i) => {
    const trimmed = line.trim();
    // Bullet points: • or - at start of line
    const isBullet = /^[•\-\*]\s+/.test(trimmed);
    const content = isBullet ? trimmed.replace(/^[•\-\*]\s+/, '') : line;

    // Bold: **text** → <strong>
    const parts: React.ReactNode[] = [];
    const boldRegex = /\*\*(.+?)\*\*/g;
    let lastIndex = 0;
    let match;
    while ((match = boldRegex.exec(content)) !== null) {
      if (match.index > lastIndex) parts.push(content.slice(lastIndex, match.index));
      parts.push(<strong key={`${i}-b-${match.index}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) parts.push(content.slice(lastIndex));
    if (parts.length === 0) parts.push(content);

    if (isBullet) {
      return <div key={i} className="flex gap-2 ml-2 my-0.5"><span className="text-red-500 flex-shrink-0">•</span><span>{parts}</span></div>;
    }
    if (trimmed === '') {
      return <div key={i} className="h-2" />;
    }
    return <div key={i}>{parts}</div>;
  });
}

type ChatMessage = { role: 'assistant' | 'user'; content: string; kind?: 'normal' | 'understanding' | 'thinking' | 'progress'; confidenceLevel?: number; stage?: string; };

interface RuleChatbotModalProps {
  open: boolean;
  onClose: () => void;
  onFinalize: (suggested: SuggestedRule) => void;
  availableColumns?: string[];
  columnSample?: unknown[];
  hasExistingRule?: boolean; // if a rule is already applied to this column, label should say "Edit rule"
  tableName?: string;
  tableMetadata?: {
    file_name: string;
    description?: string;
    columns: Array<{
      name: string;
      type: string;
      description?: string;
    }>;
  };
}

const INITIAL_PROMPT = 'Describe your rule in plain language. I’ll help refine it into a formal data quality rule.';

export default function RuleChatbotModal({ open, onClose, onFinalize, availableColumns = [], columnSample = [], hasExistingRule = false, tableName, tableMetadata }: RuleChatbotModalProps): JSX.Element | null {
  const { t } = useTranslate();
  const THINKING_COLLAPSE_DELAY_MS = 700; // slightly longer pause before auto-collapse
  const [messages, setMessages] = useState<ChatMessage[]>([{ role: 'assistant', content: INITIAL_PROMPT }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reasoningId, setReasoningId] = useState<string | undefined>(undefined);
  const [targetColumn, setTargetColumn] = useState<string | undefined>(undefined);
  const [llmUnderstanding, setLlmUnderstanding] = useState<string | undefined>(undefined);
  const [confidence, setConfidence] = useState<number | undefined>(undefined); // kept for state completeness, not displayed
  const [suggestedRule, setSuggestedRule] = useState<SuggestedRule | undefined>(undefined);
  const [isStaticRule, setIsStaticRule] = useState(false); // Track if current rule is static DQ rule
  const [responseType, setResponseType] = useState<string | undefined>(undefined); // Track response type for display logic
  const [solutionContent, setSolutionContent] = useState<any>(null); // Store final_result.solution object
  const [validationData, setValidationData] = useState<any>(undefined); // Store validation results
  const [executionResults, setExecutionResults] = useState<any>(undefined); // Store execution results for custom_sql_required
  const [generatedSql, setGeneratedSql] = useState<string | undefined>(undefined); // Store generated SQL text when available
  const [lastCompletionResult, setLastCompletionResult] = useState<WebSocketCompletionResult | null>(null);
  const [activeProgressIndex, setActiveProgressIndex] = useState<number | null>(null); // Track which progress message should blink
  const [showProgressMessages, setShowProgressMessages] = useState(true); // Control visibility of progress messages
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const { activeLanguage: selectedLanguage } = useLanguage();
  // Control thinking process visibility per question
  const [questionThinkingVisible, setQuestionThinkingVisible] = useState<Record<string, boolean>>({});
  const [clientId] = useState<string>(() => generateClientId());
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Unique key to identify this modal instance by table + column
  const modalKey = useMemo(() => {
    const col = targetColumn || availableColumns?.[0] || 'unknown_column';
    const tbl = tableName || tableMetadata?.file_name || 'data_table';
    return `${tbl}::${col}`;
  }, [targetColumn, availableColumns, tableName, tableMetadata?.file_name]);

  // Enforce single open instance across columns: if another is active, auto-close this one
  useEffect(() => {
    if (open) {
      if (ACTIVE_MODAL_KEY && ACTIVE_MODAL_KEY !== modalKey) {
        // Another modal is already active; prevent multiple thinking processes
        onClose();
        return;
      }
      ACTIVE_MODAL_KEY = modalKey;
    } else {
      if (ACTIVE_MODAL_KEY === modalKey) {
        ACTIVE_MODAL_KEY = null;
      }
    }
  }, [open, modalKey, onClose]);
  // Execution Results scroll reference (no slider control)
  const execScrollRef = useRef<HTMLDivElement | null>(null);

  // Tab state for Sample Values and Generated SQL dialog
  const [activeTab, setActiveTab] = useState<'samples' | 'sql'>('samples');

  // Effect to set default tab for static rules
  useEffect(() => {
    if (isStaticRule) {
      setActiveTab('sql'); // Default to Rule Configuration for static rules
    }
  }, [isStaticRule]);

  // Conversation history state for multi-turn interactions (as per API documentation)
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: 'user' | 'assistant', content: string, timestamp?: string, query_type?: string }>>([]);

  // Store thinking steps per question - keyed by question content for isolation
  const [questionThinkingSteps, setQuestionThinkingSteps] = useState<Record<string, ChatMessage[]>>({});

  // Track session metrics for better user experience
  const [sessionMetrics, setSessionMetrics] = useState<{ totalQueries: number, successfulQueries: number, avgProcessingTime: number }>({ totalQueries: 0, successfulQueries: 0, avgProcessingTime: 0 });

  // Store query start time for performance tracking
  const [queryStartTime, setQueryStartTime] = useState<number | null>(null);

  // Helper function to get the current user question
  const getCurrentQuestion = useCallback(() => {
    const userMessages = messages.filter(m => m.role === 'user');
    return userMessages[userMessages.length - 1]?.content || '';
  }, [messages]);

  // Helper function to add assistant response to conversation history with enriched context
  const addAssistantToHistory = (content: string, responseType: string, success: boolean = true, additionalContext: any = {}) => {
    const processingTime = queryStartTime ? performance.now() - queryStartTime : 0;
    const assistantEntry = {
      role: 'assistant' as const,
      content,
      timestamp: new Date().toISOString(),
      query_type: responseType,
      processing_time: processingTime,
      success,
      confidence: additionalContext.confidence,
      target_column: targetColumn,
      ...additionalContext
    };
    setConversationHistory(prev => [...prev.slice(-10), assistantEntry]);
  };

  // side preview panel (no separate modal)

  useEffect(() => {
    if (!open) {
      // reset state when closed
      setMessages([{ role: 'assistant', content: INITIAL_PROMPT }]);
      setInput('');
      setIsLoading(false);
      setReasoningId(undefined);
      setTargetColumn(undefined);
      setLlmUnderstanding(undefined);
      setConfidence(undefined);
      setSuggestedRule(undefined);
      setIsStaticRule(false); // Reset static rule flag
      setResponseType(undefined); // Reset response type
      setSolutionContent(null); // Reset solution content
      setExecutionResults(undefined);
      setGeneratedSql(undefined);
      setActiveProgressIndex(null);
      setIsAtBottom(true);

      // Reset conversation context and session metrics
      setConversationHistory([]);
      setSessionMetrics({ totalQueries: 0, successfulQueries: 0, avgProcessingTime: 0 });
      setQueryStartTime(null);

      // Reset conversation history and session metrics
      setConversationHistory([]);
      setSessionMetrics({ totalQueries: 0, successfulQueries: 0, avgProcessingTime: 0 });

      // Disconnect WebSocket when modal closes
      if (isConnected) {
        dqRulesWebSocketService.disconnect();
        setIsConnected(false);
      }
    } else {
      // Connect WebSocket when modal opens - with guard to prevent duplicate connections
      if (!isConnected && !dqRulesWebSocketService.isConnected()) {
        connectWebSocket();
      }
    }
  }, [open, isConnected]);

  // Initialize working column from parent context (first available column)
  useEffect(() => {
    if (open && !targetColumn && availableColumns && availableColumns.length > 0) {
      setTargetColumn(availableColumns[0]);
    }
  }, [open, targetColumn, availableColumns]);

  useEffect(() => {
    if (open && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [open]);

  const [hasAutoCollapsed, setHasAutoCollapsed] = useState<boolean>(false); // Track if we already auto-collapsed

  // Auto-manage thinking process visibility: open when thinking starts, close after final step + understanding is displayed
  useEffect(() => {
    if (isLoading) {
      // Auto-show thinking process for current question when new thinking messages arrive
      const currentQ = getCurrentQuestion();
      if (currentQ) {
        setQuestionThinkingVisible(prev => ({ ...prev, [currentQ]: true }));
      }
      setHasAutoCollapsed(false); // Reset auto-collapse flag for new query
    } else {
      // Close thinking process after loading is complete
      const hasFinalThinkingShown = messages.some(m => m.kind === 'thinking' && m.stage === 'complete');
      const hasAnyThinkingMessages = messages.some(m => m.kind === 'thinking');

      const currentQ = getCurrentQuestion();
      const currentQuestionVisible = currentQ ? questionThinkingVisible[currentQ] : false;

      // Auto-collapse if either:
      // 1. Final thinking step was shown (original logic)
      // 2. Loading is complete and thinking process is visible (for simple messages)
      if ((hasFinalThinkingShown || (hasAnyThinkingMessages && currentQuestionVisible)) && !hasAutoCollapsed) {
        // Smoothly auto-scroll to the latest step before collapsing
        setTimeout(() => {
          // Ensure we scroll even if user is not at bottom (smooth)
          const container = chatRef.current;
          if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
          }
        }, 50);

        setTimeout(() => {
          if (currentQ) {
            setQuestionThinkingVisible(prev => ({ ...prev, [currentQ]: false }));
          }
          setHasAutoCollapsed(true); // Mark as auto-collapsed to prevent future auto-closes
        }, THINKING_COLLAPSE_DELAY_MS);
      }
    }
  }, [isLoading, messages, questionThinkingVisible, hasAutoCollapsed]);

  const preview = useMemo(() => {
    // Always show rule_description in the Rule Description section
    const business = suggestedRule?.rule_description || '(not set)';
    const logic = suggestedRule ? JSON.stringify(suggestedRule.parameters) : '(incomplete)';
    return { business, logic };
  }, [suggestedRule]);
  // Helper: extract SQL from a description when it contains fenced code or inline SQL
  const extractSqlFromDescription = (desc?: string): string | null => {
    if (!desc || typeof desc !== 'string') return null;
    // Try fenced block first
    const fencedMatch = desc.match(/```\s*sql\s*\n([\s\S]*?)```/i) || desc.match(/```\s*\n([\s\S]*?)```/i);
    if (fencedMatch && fencedMatch[1]) {
      return fencedMatch[1].trim();
    }
    // Fallback: detect common SQL keywords
    const trimmed = desc.trim();
    if (/(select\s|with\s|insert\s|update\s|delete\s|create\s|alter\s|drop\s)/i.test(trimmed)) {
      return trimmed;
    }
    return null;
  };
  const showPreview = useMemo(() => {
    const show = Boolean(suggestedRule) || Boolean(llmUnderstanding) || Boolean(executionResults);
    return show;
  }, [suggestedRule, llmUnderstanding, executionResults]);

  // Ensure Result column (dq_validation_result) is rendered last and can be pinned on the right
  const orderedExecCols = useMemo(() => {
    const cols: string[] = Array.isArray(executionResults?.column_names)
      ? [...executionResults!.column_names]
      : [];
    const idx = cols.indexOf('dq_validation_result');
    if (idx >= 0) {
      cols.splice(idx, 1);
      cols.push('dq_validation_result');
    }
    return cols;
  }, [executionResults?.column_names]);

  // Steps completed badge (count thinking messages)
  const thinkingStepsCount = useMemo(() => {
    return messages.filter(m => m.kind === 'thinking').length;
  }, [messages]);



  // Custom close handler that ensures WebSocket disconnection
  const handleClose = useCallback(() => {
    // Immediately disconnect WebSocket before closing
    if (isConnected || dqRulesWebSocketService.isConnected()) {
      dqRulesWebSocketService.disconnect();
      setIsConnected(false);
    }

    // Call the parent's close handler
    onClose();
  }, [isConnected, onClose]);

  // Handle ESC key to close modal and disconnect WebSocket
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        event.preventDefault();
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, handleClose]);

  // WebSocket connection and event handling
  const connectWebSocket = async () => {
    try {
      // Close any existing connection first to ensure fresh connection
      if (isConnected || dqRulesWebSocketService.isConnected()) {
        dqRulesWebSocketService.disconnect();
        setIsConnected(false);
        // Small delay to ensure clean disconnect
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      await dqRulesWebSocketService.connect(clientId);
      setIsConnected(true);

      // IMPORTANT: Clear any existing callbacks to prevent duplicates
      dqRulesWebSocketService.clearCallbacks();

      // Handle progress updates
      dqRulesWebSocketService.onProgress((update: WebSocketProgressUpdate) => {
        // Filter out connection messages
        if (update.message?.includes('WebSocket connection established') ||
          update.message?.includes('Ready to process queries')) {
          return; // Don't show connection messages to user
        }

        // Show thinking process steps to keep users engaged
        setMessages(prev => {
          // Build clean thinking content
          const thinkingContent = update.user_friendly_thinking
            || update.message?.replace(/^\[.*?\]/, '').trim() // Remove technical prefixes like [PROCESSING] 
            || t('Analyzing your request...');

          // Get current question to store thinking steps per question
          const userMessages = prev.filter(m => m.role === 'user');
          const currentQuestion = userMessages[userMessages.length - 1]?.content || '';

          // For new thinking steps, we need to ensure we only keep the current question's thinking steps
          // Find the last user message to determine the current question boundary
          const lastUserMessageIndex = [...prev].reverse().findIndex(m => m.role === 'user');
          const currentQuestionStartIndex = lastUserMessageIndex >= 0 ? prev.length - 1 - lastUserMessageIndex : 0;

          // Keep messages up to the current question (including the user message)
          const messagesUpToCurrentQuestion = prev.slice(0, currentQuestionStartIndex + 1);

          // Get only thinking messages from the current question (after the last user message)
          const currentQuestionMessages = prev.slice(currentQuestionStartIndex + 1);
          const currentThinkingMessages = currentQuestionMessages.filter(m => m.kind === 'thinking');

          // Check if this thinking content already exists in current question's thinking steps
          const alreadyExists = currentThinkingMessages.some(msg =>
            msg.content === thinkingContent
          );

          if (alreadyExists) {
            // This exact thinking content already exists - SKIP COMPLETELY
            return prev;
          }

          const thinkingMessage: ChatMessage = {
            role: 'assistant',
            content: thinkingContent,
            kind: 'thinking',
            stage: update.stage
          };

          // Store thinking step for this specific question (ONLY in questionThinkingSteps, not in main messages)
          if (currentQuestion) {
            setQuestionThinkingSteps(prevSteps => ({
              ...prevSteps,
              [currentQuestion]: [...(prevSteps[currentQuestion] || []), thinkingMessage]
            }));
          }

          // Don't add thinking messages to main messages array - they're displayed separately via questionThinkingSteps
          return prev;
        });
      });

      // Handle completion
      dqRulesWebSocketService.onCompletion((result: WebSocketCompletionResult) => {
        setIsLoading(false);
        setLastCompletionResult(result);

        // Calculate processing time and update metrics
        const endTime = performance.now();
        const processingTime = endTime - (queryStartTime || endTime);

        setSessionMetrics(prev => ({
          ...prev,
          successfulQueries: result.final_result?.success ? prev.successfulQueries + 1 : prev.successfulQueries,
          avgProcessingTime: (prev.avgProcessingTime * (prev.totalQueries - 1) + processingTime) / prev.totalQueries
        }));

        if (result.final_result && result.final_result.success) {
          // Append a final thinking step to signal completion; understanding is shown after collapse
          setMessages(prev => {
            // Find the last user message to determine the current question boundary
            const lastUserMessageIndex = [...prev].reverse().findIndex(m => m.role === 'user');
            const currentQuestionStartIndex = lastUserMessageIndex >= 0 ? prev.length - 1 - lastUserMessageIndex : 0;

            // Get only thinking messages from the current question (after the last user message)
            const currentQuestionMessages = prev.slice(currentQuestionStartIndex + 1);
            const currentThinkingMessages = currentQuestionMessages.filter(m => m.kind === 'thinking');

            const finalStep: ChatMessage = {
              role: 'assistant',
              content: t('Thinking complete. Generating rule understanding…'),
              kind: 'thinking',
              stage: 'complete'
            };

            const alreadyHasFinal = currentThinkingMessages.some(m => m.kind === 'thinking' && m.stage === 'complete');
            return alreadyHasFinal ? prev : [...prev, finalStep];
          });

          // Handle new unified API structure
          if (result.final_result.solution) {
            const solution = result.final_result.solution;

            // Handle SQL generation scenario
            if (result.final_result.response_type === 'sql_generated' && solution.type === 'sql_query') {
              setResponseType('sql_generated');

              // For SQL generation, always show execution results if available
              if (result.final_result.validation_results?.execution_results?.sample_rows) {
                // Build a normalized executionResults object expected by the UI
                const sampleRows = result.final_result.validation_results.execution_results.sample_rows;
                const columnNames = (Array.isArray(sampleRows) && sampleRows.length > 0
                  ? Object.keys(sampleRows[0])
                  : []);
                setExecutionResults({
                  column_names: columnNames,
                  sample_rows: sampleRows,
                  total_rows: Array.isArray(sampleRows) ? sampleRows.length : 0
                });
                // Capture SQL if provided by backend
                const sqlText = result.final_result.solution?.content || result.final_result.sql_query || result.final_result.sql_content || extractSqlFromDescription(result.final_result.rule_description);
                if (sqlText) setGeneratedSql(sqlText);
                setLlmUnderstanding(result.final_result.llm_understanding); // Rule Understanding
                setReasoningId(clientId);
                // Also populate Rule Configuration with SQL and Description with understanding
                setSuggestedRule({
                  rule_type: 'ai_generated_rule' as any,
                  parameters: {
                    llm_understanding: result.final_result.llm_understanding || '',
                    custom_sql: sqlText || '', // Include the extracted SQL
                    validation_description: result.final_result.llm_understanding || '',
                    generated_by_llm: true,
                    original_prompt: result.final_result.rule_description || ''
                  },
                  rule_description: result.final_result.rule_description, // Rule Description
                  column_name: targetColumn
                });
                setIsStaticRule(false); // This is SQL generation, not static rule

                setMessages(prev => {
                  // Keep thinking message and add completion messages
                  const completionMessages: ChatMessage[] = [];
                  const understandingText = result.final_result.llm_understanding;
                  if (understandingText) {
                    completionMessages.push({
                      role: 'assistant',
                      kind: 'understanding',
                      content: understandingText
                    });

                    // Add assistant response to conversation history with enriched context
                    addAssistantToHistory(
                      '', // Blank for now
                      result.final_result.response_type || 'sql_generated',
                      true,
                      { confidence: result.final_result.confidence, sql_query: result.final_result.sql_query }
                    );
                  }
                  setActiveProgressIndex(null);
                  return [...prev, ...completionMessages];
                });
              } else {
                // No execution results - try to map SQL to a rule only as fallback
                const mappedRule = mapWebSocketResultToRule(result, targetColumn);
                if (mappedRule) {
                  setSuggestedRule(mappedRule);
                  setIsStaticRule(false); // This is SQL-based, not static rule
                  setLlmUnderstanding(result.final_result.llm_understanding);
                  setConfidence(result.final_result.confidence || result.final_result.confidence_score || 0.8);
                  setReasoningId(clientId);
                  setExecutionResults(undefined); // Clear execution results when showing rule
                  // Capture SQL even if we mapped to rule, for transparency
                  const sqlText = result.final_result.solution?.content || result.final_result.sql_query || result.final_result.sql_content || extractSqlFromDescription(result.final_result.rule_description);
                  if (sqlText) setGeneratedSql(sqlText);

                  setMessages(prev => {
                    const completionMessages: ChatMessage[] = [];
                    const understandingText = result.final_result.llm_understanding;
                    if (understandingText) {
                      completionMessages.push({
                        role: 'assistant',
                        kind: 'understanding',
                        content: understandingText
                      });
                    }
                    setActiveProgressIndex(null);
                    return [...prev, ...completionMessages];
                  });
                } else {
                  // Can't map to rule and no execution results - show as custom SQL understanding only
                  setLlmUnderstanding(result.final_result.llm_understanding); // Rule Understanding
                  setReasoningId(clientId);
                  const sqlText2 = result.final_result.solution?.content || result.final_result.sql_query || result.final_result.sql_content || extractSqlFromDescription(result.final_result.rule_description);
                  setSuggestedRule({
                    rule_type: 'sql_rule' as any,
                    parameters: { llm_understanding: result.final_result.llm_understanding || '' },
                    rule_description: result.final_result.rule_description, // Rule Description
                    column_name: targetColumn
                  });
                  setIsStaticRule(false); // This is SQL-based, not static rule
                  setExecutionResults(undefined);
                  if (sqlText2) setGeneratedSql(sqlText2);

                  setMessages(prev => {
                    // Keep thinking messages and add completion messages
                    const completionMessages: ChatMessage[] = [];
                    const understandingText = result.final_result.llm_understanding;
                    if (understandingText) {
                      completionMessages.push({
                        role: 'assistant',
                        kind: 'understanding',
                        content: understandingText
                      });
                    }
                    setActiveProgressIndex(null);
                    return [...prev, ...completionMessages];
                  });
                }
              }
            }

            // Handle static DQ rules scenario
            else if (result.final_result.response_type === 'dq_rules_recommended' && solution.type === 'dq_rule') {
              setIsStaticRule(true); // Mark as static rule for UI layout
              setResponseType('dq_rules_recommended');

              const mappedRule: SuggestedRule = {
                rule_type: solution.rule_type as any,
                rule_description: result.final_result.rule_description, // Rule Description
                parameters: solution.parameters || {},
                column_name: solution.column_name || targetColumn
              };

              setSuggestedRule(mappedRule);
              setIsStaticRule(true); // Mark as static rule

              // Store solution object for Rule Configuration display in static scenarios
              if (result.final_result.solution) {
                setSolutionContent(result.final_result.solution);
              }
              setLlmUnderstanding(result.final_result.llm_understanding); // Rule Understanding - direct from final_result
              setConfidence(result.final_result.confidence || 0.8);
              setReasoningId(clientId);

              // Set validation data from execution results
              if (result.final_result.validation_results?.execution_results?.sample_rows) {
                const sampleRows = result.final_result.validation_results.execution_results.sample_rows;
                const passedCount = sampleRows.filter((row: any) => row.dq_validation_result === 'PASS').length;
                const failedCount = sampleRows.length - passedCount;

                // Derive column names for summary and table
                const columnNames = (Array.isArray(sampleRows) && sampleRows.length > 0
                  ? Object.keys(sampleRows[0])
                  : []);

                // Align with UI expectations: overall_pass_rate (number), total_failed_records, status, totals
                setValidationData({
                  status: 'completed',
                  overall_pass_rate: Math.round((passedCount / (sampleRows.length || 1)) * 100),
                  total_failed_records: failedCount,
                  total_rows_validated: sampleRows.length,
                  total_columns_validated: columnNames.length
                });

                // Also populate execution results table using real sample rows
                setExecutionResults({
                  column_names: columnNames,
                  sample_rows: sampleRows,
                  total_rows: Array.isArray(sampleRows) ? sampleRows.length : 0
                });
              }

              setMessages(prev => {
                // Keep thinking messages and add completion messages
                const completionMessages: ChatMessage[] = [];
                const understandingText = result.final_result.llm_understanding; // Rule Understanding - direct from final_result
                if (understandingText) {
                  completionMessages.push({
                    role: 'assistant',
                    kind: 'understanding',
                    content: understandingText
                  });

                  // Add to conversation history
                  addAssistantToHistory(
                    result.final_result.llm_understanding,
                    'static_dq_rules',
                    true,
                    { confidence: result.final_result.confidence, rule_type: solution.rule_type }
                  );
                }
                setActiveProgressIndex(null);
                return [...prev, ...completionMessages];
              });
            }
          }

          // Legacy handling for old API responses
          else if ((result.final_result as any).classification_results?.classification === 'custom_sql_required' && (result.final_result as any).execution_results) {
            // Store execution results for table population
            setExecutionResults((result.final_result as any).execution_results);
            // Capture SQL if present on legacy path
            const sqlText = result.final_result.sql_query || result.final_result.sql_content || extractSqlFromDescription(result.final_result.rule_description);
            if (sqlText) setGeneratedSql(sqlText);
            setLlmUnderstanding(result.final_result.llm_understanding || 'Custom SQL analysis completed');
            setReasoningId(clientId);
            setSuggestedRule({
              rule_type: 'sql_rule' as any,
              parameters: { llm_understanding: result.final_result.llm_understanding || '' },
              rule_description: result.final_result.rule_description || 'SQL-based validation', // Rule Description
              column_name: targetColumn
            });
            setIsStaticRule(false); // This is SQL-based, not static rule

            const baseMessage = result.final_result.llm_understanding || 'Analysis completed. The data requires custom SQL handling.';
            const cleanMessage = baseMessage.replace(/\n\nSQL Query:\n.*$/s, '');

            setMessages(prev => {
              // Keep thinking messages and add completion messages
              const completionMessages: ChatMessage[] = [];
              if (result.final_result.llm_understanding) {
                completionMessages.push({
                  role: 'assistant',
                  kind: 'understanding',
                  content: cleanMessage
                });

                // Add to conversation history
                addAssistantToHistory(
                  cleanMessage,
                  'custom_sql_required',
                  true,
                  { sql_query: result.final_result.sql_query }
                );
              }
              setActiveProgressIndex(null);
              return [...prev, ...completionMessages];
            });
          }

          // Legacy DQ rules recommendation response
          else if ((result.final_result.response_type as any) === 'dq_rules_recommended' && result.final_result.suggested_rules?.length > 0) {
            setIsStaticRule(true); // Mark as static rule for UI layout 
            setResponseType('dq_rules_recommended');

            const firstRule = result.final_result.suggested_rules[0];
            const mappedRule: SuggestedRule = {
              rule_type: firstRule.rule_type as any,
              rule_description: firstRule.rule_description,
              parameters: firstRule.parameters,
              column_name: firstRule.column_name || targetColumn
            };

            setSuggestedRule(mappedRule);
            setIsStaticRule(false); // This is rule_suggested, not static rule
            setLlmUnderstanding(result.final_result.llm_understanding); // Rule Understanding
            setConfidence(result.final_result.confidence_score);
            setReasoningId(clientId);

            if (result.final_result.dq_sample_validation?.summary) {
              setValidationData(result.final_result.dq_sample_validation.summary);
            }

            setMessages(prev => {
              // Keep thinking messages and add completion messages
              const completionMessages: ChatMessage[] = [];
              const understandingText = result.final_result.llm_understanding;
              if (understandingText) {
                completionMessages.push({
                  role: 'assistant',
                  kind: 'understanding',
                  content: understandingText
                });

                // Add to conversation history
                addAssistantToHistory(
                  result.final_result.llm_understanding,
                  'dq_rules_recommended',
                  true,
                  { confidence: result.final_result.confidence_score, rule_type: firstRule.rule_type }
                );
              }
              setActiveProgressIndex(null);
              return [...prev, ...completionMessages];
            });
          }

          // Legacy SQL generation handling
          else if (result.final_result.response_type === 'sql_generated' && (result.final_result as any).classification_results?.classification !== 'custom_sql_required') {
            setResponseType('sql_generated');

            const mappedRule = mapWebSocketResultToRule(result, targetColumn);

            if (mappedRule) {
              setSuggestedRule(mappedRule);
              setIsStaticRule(false); // This is SQL generation, not static rule
              setLlmUnderstanding(result.final_result.llm_understanding); // Rule Understanding
              setConfidence(result.final_result.confidence_score);
              setReasoningId(clientId);
              const sqlText = result.final_result.sql_query || result.final_result.sql_content || extractSqlFromDescription(result.final_result.rule_description);
              if (sqlText) setGeneratedSql(sqlText);

              setMessages(prev => {
                const responseContent = result.final_result.llm_understanding || result.final_result.explanation;

                // Add to conversation history
                addAssistantToHistory(
                  responseContent,
                  'sql_generated',
                  true,
                  { confidence: result.final_result.confidence_score, sql_query: result.final_result.sql_query }
                );

                return [...prev, {
                  role: 'assistant',
                  kind: 'understanding',
                  content: responseContent
                }];
              });
            } else {
              // Show SQL result and ask for clarification about creating a rule
              const sqlQuery = result.final_result.sql_query || result.final_result.sql_content || extractSqlFromDescription(result.final_result.rule_description);
              const explanation = result.final_result.explanation || 'I generated SQL but need more details to create a specific data quality rule.';
              if (sqlQuery) setGeneratedSql(sqlQuery);
              const clarificationContent = `${explanation}${sqlQuery ? `\n\nSQL Generated: ${sqlQuery}` : ''}\n\nTo create a data quality rule, please specify what validation or constraint you want to apply to the data.`;

              // Add to conversation history
              addAssistantToHistory(
                clarificationContent,
                'clarification_needed',
                false,
                { sql_query: sqlQuery }
              );

              setMessages(prev => {
                return [...prev, {
                  role: 'assistant',
                  content: clarificationContent
                }];
              });
            }
          } else {
            // Prioritize LLM understanding from direct field
            const explanation = result.final_result.llm_understanding || result.final_result.explanation || 'I need more details to create a specific data quality rule.';

            // Add to conversation history
            addAssistantToHistory(
              explanation,
              result.final_result.response_type || 'unknown',
              false
            );

            setMessages(prev => {
              // Keep thinking messages and add completion messages
              return [...prev, {
                role: 'assistant',
                content: explanation
              }];
            });
          }
        } else {
          // Prioritize LLM understanding from direct field even for unsuccessful results
          const explanation = result.final_result?.llm_understanding || result.final_result?.explanation || 'I encountered an issue processing your request. Please try again with more specific details about the data quality rule you need.';

          // Add to conversation history
          addAssistantToHistory(
            explanation,
            result.final_result?.response_type || 'error',
            false
          );

          setMessages(prev => {
            // Keep thinking messages and add response message
            return [...prev, {
              role: 'assistant',
              content: explanation
            }];
          });
        }
      });

      // Handle errors
      dqRulesWebSocketService.onError((error: WebSocketErrorResult) => {
        setIsLoading(false);
        const errorContent = `Error: ${error.message}. Please try again.`;

        // Add to conversation history
        addAssistantToHistory(errorContent, 'error', false);

        setMessages(prev => {
          // Keep thinking messages and add error message
          return [...prev, {
            role: 'assistant',
            content: errorContent
          }];
        });
      });

    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      const connectionErrorContent = 'Failed to connect to the AI service. Please try again later.';

      // Add to conversation history
      addAssistantToHistory(connectionErrorContent, 'connection_error', false);

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: connectionErrorContent
      }]);
    }
  };

  const asConversationHistory = () => {
    // Convert messages to conversation format if needed for context
    return messages.map(m => ({
      role: m.role,
      content: m.content,
      timestamp: new Date().toISOString()
    }));
  };

  const callAnalyze = async (userMessage: string) => {
    if (!isConnected) {
      setMessages(prev => [...prev.filter(m => m.kind !== 'thinking'), {
        role: 'assistant',
        content: 'Not connected to AI service. Please wait while we reconnect...'
      }]);
      await connectWebSocket();
      return;
    }

    setIsLoading(true);

    // Track query start time for performance metrics
    const startTime = performance.now();
    setQueryStartTime(startTime);

    // Update session metrics
    setSessionMetrics(prev => ({ ...prev, totalQueries: prev.totalQueries + 1 }));

    // Add user message to conversation history with enriched context
    const userEntry = {
      role: 'user' as const,
      content: userMessage,
      timestamp: new Date().toISOString(),
      query_type: 'dq_rule_creation',
      target_column: targetColumn,
      context: {
        session_query_count: sessionMetrics.totalQueries + 1,
        current_table: tableName || 'data_table',
        available_columns: availableColumns?.slice(0, 5) // Limit for context size
      }
    };
    setConversationHistory(prev => [...prev.slice(-10), userEntry]); // Keep last 10 for API efficiency

    try {
      // Build table metadata with column_description as dictionary of ColumnMetadata objects per new API spec
      const columnDescriptionDict: Record<string, any> = {};

      if (tableMetadata?.columns) {
        // Convert columns array to dictionary format with enhanced metadata
        tableMetadata.columns.forEach(col => {
          columnDescriptionDict[col.name] = {
            data_type: col.type || 'string',
            sample_values: columnSample?.slice(0, 10) || [], // Add sample values from columnSample prop
            description: col.description || `Column: ${col.name}`,
            has_quality_issues: false, // Default to false, can be enhanced with actual quality analysis
            dq_rules: null // Default to null, can be populated with existing rules
          };
        });
      } else {
        // Fallback: create from availableColumns with sample data
        availableColumns.forEach(colName => {
          columnDescriptionDict[colName] = {
            data_type: 'string', // Default type
            sample_values: columnSample?.slice(0, 10) || [],
            description: `Column: ${colName}`,
            has_quality_issues: false,
            dq_rules: null
          };
        });
      }

      const metadata = {
        file_name: tableMetadata?.file_name || tableName || 'data_table',
        description: tableMetadata?.description || 'Data quality rule creation context',
        column_description: columnDescriptionDict // API expects dictionary format
      };

      const request: WebSocketTextToSQLRequest = {
        natural_language_query: `Create a data quality rule: ${userMessage}${targetColumn ? ` for column ${targetColumn}` : ''}`,
        table_name: tableName || 'data_table', // Required field per API spec
        table_metadata: metadata,
        user_email: authManager.getUserEmail(),
        ...(selectedLanguage !== 'en' && { target_language: selectedLanguage }),
      };

      dqRulesWebSocketService.sendQuery(request);
    } catch (error) {
      console.error('Error sending WebSocket query:', error);
      setIsLoading(false);
      setMessages(prev => {
        const withoutThinking = prev.filter(m => m.kind !== 'thinking');
        return [...withoutThinking, {
          role: 'assistant',
          content: 'Failed to send query to AI service. Please try again.'
        }];
      });
    }
  };

  const handleSend = async () => {
    if (isLoading) return; // Prevent multiple calls while loading
    const content = input.trim();
    if (!content) return;

    // Add user message to conversation history
    setConversationHistory(prev => [...prev.slice(-10), {
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
      query_type: targetColumn ? 'column_specific' : 'general'
    }]);

    setMessages(prev => {
      // Remove any previous thinking messages and add new user question + thinking
      const cleanMessages = prev.filter(m => m.kind !== 'thinking');
      return [...cleanMessages, { role: 'user', content }, { role: 'assistant', content: 'Data Doctor is thinking...', kind: 'thinking' }];
    });
    setInput('');

    // If user picks a column via chat, update quick context
    if (!targetColumn && availableColumns.includes(content)) {
      setTargetColumn(content);
    }

    // Small delay to ensure thinking message is visible before WebSocket processing
    await new Promise(resolve => setTimeout(resolve, 500));

    // Call backend analyze endpoint using the latest user message
    void callAnalyze(content);
  };

  const handleStop = () => {
    if (isConnected && isLoading) {
      dqRulesWebSocketService.disconnect();
      setIsLoading(false);
      setMessages(prev => {
        // Remove the last "Data Doctor is thinking..." message and add a stopped message
        const filtered = prev.filter(msg => !(msg.kind === 'thinking' || msg.content === 'Data Doctor is thinking...'));
        return [...filtered, { role: 'assistant', content: 'Process stopped by user. Feel free to ask another question.' }];
      });
    }
  };

  const pickColumn = async (col: string) => {
    if (isLoading) return; // Prevent multiple calls while loading

    // Add user column selection to conversation history
    setConversationHistory(prev => [...prev.slice(-10), {
      role: 'user',
      content: col,
      timestamp: new Date().toISOString(),
      query_type: 'column_selection'
    }]);

    setMessages(prev => {
      // Remove any previous thinking messages and add new column selection + thinking
      const cleanMessages = prev.filter(m => m.kind !== 'thinking');
      return [...cleanMessages, { role: 'user', content: col }, { role: 'assistant', content: 'Data Doctor is thinking...', kind: 'thinking' }];
    });
    setTargetColumn(col);

    // Small delay to ensure thinking message is visible
    await new Promise(resolve => setTimeout(resolve, 500));

    void callAnalyze(col);
  };

  const refine = () => {
    // Keep conversation going; next send will call the API again with conversation_history + reasoning_id
    setMessages(prev => [...prev, { role: 'assistant', content: 'What should I adjust? You can change the column or describe the condition differently.' }]);
  };

  // Generate SQL for static rules to show users the underlying query
  const generateStaticRuleSQL = (rule: SuggestedRule): string => {
    const columnName = rule.column_name;
    const tableName = tableMetadata?.file_name || 'your_table';

    switch (rule.rule_type) {
      case 'null_check':
        return `SELECT *,\n  CASE \n    WHEN ${columnName} IS NULL THEN 'FAIL'\n    ELSE 'PASS'\n  END AS dq_validation_result\nFROM ${tableName};`;

      case 'range_check':
        const min = rule.parameters?.min_value || 0;
        const max = rule.parameters?.max_value || 100;
        return `SELECT *,\n  CASE \n    WHEN ${columnName} BETWEEN ${min} AND ${max} THEN 'PASS'\n    ELSE 'FAIL'\n  END AS dq_validation_result\nFROM ${tableName};`;

      case 'length_check':
        const minLength = rule.parameters?.min_length || 1;
        const maxLength = rule.parameters?.max_length || 255;
        return `SELECT *,\n  CASE \n    WHEN LENGTH(${columnName}) BETWEEN ${minLength} AND ${maxLength} THEN 'PASS'\n    ELSE 'FAIL'\n  END AS dq_validation_result\nFROM ${tableName};`;

      default:
        return `SELECT *,\n  -- ${rule.rule_type} validation logic\n  'PASS' AS dq_validation_result\nFROM ${tableName};`;
    }
  };

  const finalize = () => {
    if (suggestedRule) {
      onFinalize(suggestedRule);
      return;
    }
    // Fallback: derive a rule from the latest completion (SQL/understanding)
    if (lastCompletionResult) {
      const mapped = mapWebSocketResultToRule(lastCompletionResult, targetColumn);
      if (mapped) {
        onFinalize(mapped);
      }
    }
  };

  // chat scroll helpers
  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
      setIsAtBottom(true);
    }
  };


  useEffect(() => {
    const container = chatRef.current;
    if (!container) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 24; // threshold
      setIsAtBottom(nearBottom);
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll on new messages only when already at bottom
  useEffect(() => {
    const container = chatRef.current;
    if (!container) return;
    if (isAtBottom) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, isAtBottom]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="rule-chatbot-title"
      className={'fixed inset-0 z-[11000] flex items-center justify-center'}
    >
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative rounded-2xl shadow-2xl w-[96vw] max-w-[1400px] h-[90vh] mx-4 overflow-hidden"
      >
        {/* Red gradient header */}
        <div className="bg-gradient-to-r from-red-600 via-rose-600 to-red-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 id="rule-chatbot-title" className="text-white text-lg font-semibold flex items-center gap-2">
                <FaUserDoctor className="text-white text-3xl" />
                {t('Create Rules with AI')}
              </h2>

              {/* Connection status indicator */}
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'
                  }`}></div>
                <span className="text-xs text-white/80">
                  {isConnected ? t('Connected') : t('Connecting...')}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {(targetColumn || (availableColumns && availableColumns[0])) && (
                <span
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-lg font-bold bg-white/20 text-white border border-white/30"
                  aria-label={`Working on column ${targetColumn || availableColumns[0]}`}
                  title={`Working on column: ${targetColumn || availableColumns[0]}`}
                >
                  {t('Column:')} <span className="notranslate">{targetColumn || availableColumns[0]}</span>
                </span>
              )}
              <button className="text-white hover:text-red-200 rounded-md p-2" onClick={handleClose} aria-label="Close">✕</button>
            </div>
          </div>
        </div>

        {/* Futuristic split layout: chat left, preview right */}
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 bg-gradient-to-br from-white via-red-50 to-white h-[calc(90vh-64px)] overflow-hidden" aria-busy={isLoading}>
          {/* Chat column */}
          <div className="flex flex-col h-full min-h-0">
            <div ref={chatRef} className="relative flex-1 overflow-auto pr-2 rounded-xl border border-red-100 bg-white/90 backdrop-blur-sm p-4 space-y-3">

              {/* Regular conversation messages with thinking box in flow */}
              {messages
                .filter(m => m.kind !== 'thinking' && m.kind !== 'progress')
                .map((message, idx, filteredMessages) => {
                  const isLastUserMessage = message.role === 'user' &&
                    (idx === filteredMessages.length - 1 || filteredMessages[idx + 1]?.role !== 'user');

                  return (
                    <React.Fragment key={idx}>
                      {/* User or Assistant Message */}
                      <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`
                          max-w-[75%] px-4 py-2 rounded-lg shadow-sm
                          ${message.role === 'user'
                            ? 'bg-red-600 text-white rounded-br-sm'
                            : message.kind === 'understanding'
                              ? 'bg-gray-50 text-gray-700 border border-gray-200 rounded-lg'
                              : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                          }
                        `}>
                          {message.kind === 'understanding' ? (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-semibold text-gray-600">{t('Rule Understanding')}</span>
                              </div>
                              <div dir="auto" className="text-sm leading-relaxed">{renderFormattedContent(message.content)}</div>
                            </div>
                          ) : (
                            <div dir="auto" className="text-sm leading-relaxed">{message.role === 'assistant' ? renderFormattedContent(message.content === INITIAL_PROMPT ? t(INITIAL_PROMPT) : message.content) : message.content}</div>
                          )}
                        </div>
                      </div>

                      {/* Thinking Box appears after user message */}
                      {isLastUserMessage && messages.some(m => m.kind === 'thinking') && (
                        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200 overflow-hidden">
                          {/* Thinking Process Header with Toggle */}
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-red-100/50 transition-colors"
                            onClick={() => {
                              const isCurrentlyVisible = questionThinkingVisible[message.content] || false;
                              setQuestionThinkingVisible(prev => ({
                                ...prev,
                                [message.content]: !isCurrentlyVisible
                              }));
                            }}
                          >
                            <div className="flex items-center gap-3">
                              {isLoading && message.content === getCurrentQuestion() && (
                                <div className="flex gap-1">
                                  <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                  <span className="inline-block h-2 w-2 rounded-full bg-red-400 animate-bounce" style={{ animationDelay: '200ms' }}></span>
                                  <span className="inline-block h-2 w-2 rounded-full bg-red-300 animate-bounce" style={{ animationDelay: '400ms' }}></span>
                                </div>
                              )}
                              <h3 className="text-sm font-semibold text-red-700">{t("Data Doctor's Thinking Process")}</h3>
                              {/* Removed steps count badge per request; all steps render below visibly */}
                            </div>
                            <div className="flex items-center gap-2">
                              {!isLoading && (
                                <span className="text-xs text-green-600 font-medium">✓ {t('Thinking Complete')}</span>
                              )}
                              <button
                                className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                                aria-label={(questionThinkingVisible[message.content] || false) ? 'Hide thinking process' : 'Show thinking process'}
                              >
                                {(questionThinkingVisible[message.content] || false) ? '▼' : '▶'}
                              </button>
                            </div>
                          </div>

                          {/* Collapsible Thinking Content */}
                          {(questionThinkingVisible[message.content] || false) && (
                            <div className="px-4 pb-4 space-y-2 max-h-60 overflow-y-auto">
                              {(questionThinkingSteps[message.content] || [])
                                .map((thinkingMessage, thinkingIdx) => (
                                  <div key={thinkingIdx} className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-red-100/50">
                                    <div className="flex-shrink-0 w-6 h-6 bg-red-200 rounded-full flex items-center justify-center text-xs font-bold text-red-700">
                                      {thinkingIdx + 1}
                                    </div>
                                    <div className="flex-1">
                                      {thinkingMessage.stage && (
                                        <div className="text-xs text-red-600 font-medium mb-1">
                                          {t(thinkingMessage.stage)}
                                        </div>
                                      )}
                                      <div className="text-sm text-red-800 leading-relaxed">{renderFormattedContent(t(thinkingMessage.content))}</div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}

              {!isAtBottom && (
                <div className="sticky bottom-2 flex gap-2 justify-end pr-2">
                  <button
                    type="button"
                    onClick={scrollToBottom}
                    className="px-3 py-1 text-xs rounded-md bg-white border border-gray-200 shadow-sm hover:bg-gray-50"
                  >
                    {t('Jump to latest')}
                  </button>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="mt-3 flex gap-2">
              <input
                aria-label="Type your message"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !isLoading) { e.preventDefault(); handleSend(); } }}
                className={`flex-1 border border-red-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-red-300 ${isLoading ? 'bg-red-50 cursor-not-allowed text-red-700' : 'bg-white'}`}
                placeholder={isLoading ? t('Data Doctor is thinking...') : t('Describe a rule or answer the question...')}
                disabled={isLoading}
              />
              {isLoading ? (
                <button
                  type="button"
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                  onClick={handleStop}
                >
                  <ImStop2 className="text-xl" />
                </button>
              ) : (
                <button
                  type="button"
                  className="p-2 text-red-600 hover:text-red-800 transition-colors"
                  onClick={handleSend}
                >
                  <TbPlayerPlayFilled className="text-xl" />
                </button>
              )}
            </div>
          </div>

          {/* Side Preview column */}
          <div className="h-full space-y-4 min-h-0 overflow-y-auto pr-2">


            {/* Rule Description card */}
            <div className="rounded-xl border-2 border-red-200 bg-white shadow-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-red-800">
                  {t('Rule Description')}
                </span>
              </div>
              {showPreview ? (
                <div className="space-y-3 text-sm">
                  <div dir="auto" className="p-3 rounded-lg bg-rose-50/60 border border-rose-200 text-gray-900 text-[0.95rem] leading-6">
                    {preview.business === '(not set)' ? t('No description available') : preview.business}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  <div className="p-3 rounded border bg-gray-50">{t('Start by describing your rule. I’ll write a clear rule description here once I have enough details.')}</div>
                </div>
              )}
            </div>

            {/* Combined Sample Values and Generated SQL Tabbed Dialog */}
            {(generatedSql || executionResults) && (
              <div className="rounded-xl border-2 border-red-200 bg-white shadow-lg">
                {/* Tab Header */}
                <div className="flex border-b border-red-200">
                  {/* For static rules, show Rule Configuration first */}
                  {isStaticRule ? (
                    <>
                      <button
                        type="button"
                        className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === 'sql'
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-red-600 hover:bg-red-50'
                          }`}
                        onClick={() => setActiveTab('sql')}
                      >
                        {t('Rule Configuration')}
                      </button>
                      <button
                        type="button"
                        className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === 'samples'
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-red-600 hover:bg-red-50'
                          }`}
                        onClick={() => setActiveTab('samples')}
                      >
                        {t('Sample Values')}
                      </button>
                    </>
                  ) : (
                    /* For non-static rules, keep original order */
                    <>
                      <button
                        type="button"
                        className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === 'samples'
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-red-600 hover:bg-red-50'
                          }`}
                        onClick={() => setActiveTab('samples')}
                      >
                        {t('Sample Values')}
                      </button>
                      <button
                        type="button"
                        className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === 'sql'
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-red-600 hover:bg-red-50'
                          }`}
                        onClick={() => setActiveTab('sql')}
                      >
                        {t('Generated SQL')}
                      </button>
                    </>
                  )}
                </div>

                {/* Tab Content */}
                <div className="p-4">
                  {/* Sample Values Tab */}
                  {activeTab === 'samples' && (
                    <div>
                      <div ref={execScrollRef} className="overflow-x-auto overflow-y-auto max-h-80 rounded-lg border border-red-200">
                        <table className="min-w-full text-sm divide-y divide-gray-200">
                          <caption className="sr-only">Execution results sample rows</caption>
                          <thead className="sticky top-0 bg-red-50">
                            <tr>
                              {executionResults ? (
                                // Dynamic columns for execution results, with Result pinned on the right
                                orderedExecCols.map((colName: string, index: number) => (
                                  <th
                                    key={colName}
                                    scope="col"
                                    className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase tracking-wider border-b border-red-200 notranslate"
                                  >
                                    {colName}
                                  </th>
                                ))
                              ) : (
                                // Fallback header for rule-based validation
                                <>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase tracking-wider border-b border-red-200">
                                    {t('Sample Value')}
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase tracking-wider border-b border-red-200">
                                    {t('Validation Result')}
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase tracking-wider border-b border-red-200">
                                    {t('Status')}
                                  </th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {executionResults ? (
                              // Dynamic rows for execution results (API-provided data)
                              Array.isArray(executionResults.sample_rows) && executionResults.sample_rows.length > 0 ? (
                                executionResults.sample_rows.map((row: any, index: number) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    {orderedExecCols.map((colName: string) => (
                                      <td key={colName} className="px-3 py-2 whitespace-nowrap border-b border-gray-100 notranslate">
                                        <span className="font-mono text-gray-900 text-xs">
                                          {row[colName] === null ? (
                                            <span className="italic text-gray-500">null</span>
                                          ) : row[colName] === '' ? (
                                            <span className="italic text-gray-500">""</span>
                                          ) : typeof row[colName] === 'boolean' ? (
                                            <span className={row[colName] ? 'text-green-700' : 'text-red-700'}>
                                              {row[colName] ? '✓' : '✗'}
                                            </span>
                                          ) : (
                                            String(row[colName])
                                          )}
                                        </span>
                                      </td>
                                    ))}
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="px-3 py-2 text-gray-500 text-center" colSpan={orderedExecCols.length}>
                                    {t('No sample data available')}
                                  </td>
                                </tr>
                              )
                            ) : suggestedRule ? (
                              // Fallback sample data for rules
                              <>
                                {suggestedRule.rule_type === 'null_check' && (
                                  <>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 px-3 font-mono text-gray-900 notranslate">"workspace_123"</td>
                                      <td className="py-2 px-3 text-gray-600">Value is present and not null</td>
                                      <td className="py-2 px-3">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                          ✓ Valid
                                        </span>
                                      </td>
                                    </tr>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 px-3 font-mono text-gray-900 italic notranslate">null</td>
                                      <td className="py-2 px-3 text-gray-600">Value is null - violates rule</td>
                                      <td className="py-2 px-3">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                          ✗ Invalid
                                        </span>
                                      </td>
                                    </tr>
                                  </>
                                )}
                                {suggestedRule.rule_type !== 'null_check' && (
                                  <tr className="border-b border-gray-100">
                                    <td className="py-2 px-3 font-mono text-gray-900 notranslate">sample_data</td>
                                    <td className="py-2 px-3 text-gray-600">Meets rule criteria</td>
                                    <td className="py-2 px-3">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                        ✓ Valid
                                      </span>
                                    </td>
                                  </tr>
                                )}
                              </>
                            ) : (
                              <tr className="border-b border-gray-100">
                                <td className="py-2 px-3 text-gray-500" colSpan={3}>
                                  {t('No validation results available')}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Sample Data Disclaimer */}
                      <div className="mt-3 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
                        <span className="font-medium">⚠️ {t('Sample Data Only:')}</span> {t('Results shown are based on sample values, not the complete dataset.')}
                      </div>
                    </div>
                  )}

                  {/* Generated SQL Tab / Rule Configuration for static rules */}
                  {activeTab === 'sql' && (
                    <div>
                      {isStaticRule && solutionContent ? (
                        /* Rule Configuration content for static rules */
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-red-800">
                              {t('Rule Configuration')}
                            </span>
                          </div>
                          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
                            {solutionContent && typeof solutionContent === 'object' ? (
                              <div className="grid grid-cols-1 gap-3 text-sm">
                                {solutionContent.rule_type && (
                                  <div>
                                    <span className="font-semibold text-red-700">{t('Rule Type:')}</span>
                                    <span className="ml-2 text-gray-800">{solutionContent.rule_type}</span>
                                  </div>
                                )}
                                {solutionContent.column_name && (
                                  <div>
                                    <span className="font-semibold text-red-700">{t('Target Column:')}</span>
                                    <span className="ml-2 text-gray-800 notranslate">{solutionContent.column_name}</span>
                                  </div>
                                )}
                                {solutionContent.type && (
                                  <div>
                                    <span className="font-semibold text-red-700">{t('Type:')}</span>
                                    <span className="ml-2 text-gray-800">{solutionContent.type}</span>
                                  </div>
                                )}
                                {solutionContent.parameters && Object.keys(solutionContent.parameters).length > 0 && (
                                  <div>
                                    <span className="font-semibold text-red-700">{t('Parameters:')}</span>
                                    <div className="ml-2 text-gray-800 mt-1">
                                      <pre className="text-xs bg-white rounded p-2 border notranslate">{JSON.stringify(solutionContent.parameters, null, 2)}</pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-800 leading-relaxed">
                                {solutionContent || t('No configuration available')}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : !isStaticRule && generatedSql ? (
                        /* SQL content for non-static rules */
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-red-800">
                              {t('SQL Query')}
                            </span>
                            <button
                              type="button"
                              className="text-xs px-3 py-1 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 font-medium transition-colors"
                              onClick={() => navigator.clipboard?.writeText(generatedSql)}
                              title="Copy SQL to clipboard"
                            >
                              {t('Copy')}
                            </button>
                          </div>
                          <pre className="text-xs bg-red-50 rounded-lg border border-red-200 p-3 overflow-auto max-h-56 whitespace-pre-wrap text-red-900 font-mono notranslate">
                            {generatedSql}
                          </pre>
                        </div>
                      ) : (
                        /* Show message if no content available */
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">{isStaticRule ? t('No rule configuration available') : t('No SQL query available')}</p>
                          <p className="text-xs mt-1">{isStaticRule ? t('Rule configuration will appear here when available') : t('Generate a query first to see SQL content')}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rule Configuration card - hidden for custom_sql_required */}
            {showPreview && suggestedRule && !executionResults && (
              <div className="rounded-xl border border-blue-200 bg-white/80 backdrop-blur-sm p-4 shadow-md">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-800">{t('Rule Configuration')}</span>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('Rule Type')}</span>
                      {(suggestedRule.parameters as any)?.sql ? (
                        <div className="mt-1 inline-flex items-center gap-2">
                          <span className="px-3 py-1 rounded bg-blue-50 text-blue-900 border border-blue-200 font-mono text-sm">CUSTOM_RULE</span>
                          <span className="px-2 py-1 rounded-full bg-violet-50 text-violet-800 border border-violet-200 text-[10px] font-semibold">locked</span>
                        </div>
                      ) : (
                        <div className="mt-1 px-3 py-2 bg-blue-50 rounded border border-blue-200 text-blue-900 font-mono text-sm">
                          {suggestedRule.rule_type}
                        </div>
                      )}
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('Column')}</span>
                      <div className="mt-1 px-3 py-2 bg-green-50 rounded border border-green-200 text-green-900 font-mono text-sm notranslate">
                        {suggestedRule.column_name || targetColumn}
                      </div>
                    </div>
                  </div>

                  {/* Parameters */}
                  <div>
                    <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('Rule Configuration')}</span>
                    {Boolean((suggestedRule.parameters as any)?.sql) || generatedSql ? (
                      <div className="mt-1">
                        <div className="text-xs font-semibold text-gray-700 mb-1">
                          {isStaticRule ? 'Generated SQL Query' : 'SQL Query (locked)'}
                        </div>
                        <pre className="w-full max-h-56 overflow-auto p-3 rounded-md bg-gray-900 text-gray-100 text-xs whitespace-pre-wrap notranslate">
                          {(suggestedRule.parameters as any)?.sql || generatedSql}
                        </pre>
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                            onClick={() => navigator.clipboard?.writeText((suggestedRule.parameters as any)?.sql || generatedSql || '')}
                            title="Copy SQL to clipboard"
                          >
                            {t('Copy SQL')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1 px-3 py-2 bg-gray-50 rounded border border-gray-200 text-gray-900 font-mono text-sm notranslate">
                        {Object.keys(suggestedRule.parameters || {}).length > 0
                          ? JSON.stringify(suggestedRule.parameters, null, 2)
                          : t('No additional parameters required')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}



            {/* Action buttons - consistent across all scenarios */}
            {showPreview && (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-6 py-2.5 text-sm rounded-xl bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-md"
                  onClick={finalize}
                  disabled={!executionResults && !suggestedRule}
                >
                  {t('Add')}
                </button>
              </div>
            )}
          </div>


        </div>
      </div>
    </div>,
    document.body
  );
}
